import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  Modal,
  TextField,
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';
import { IoClose as Close } from 'react-icons/io5';
import {
  IoInformationCircleOutline as DetailsIcon,
  IoDocumentTextOutline as RemarksIcon,
} from 'react-icons/io5';
import { ReactComponent as Edit } from '@Assets/editIcon.svg';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import BorderWhiteButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderWhiteButtonV2';
import ErrorHandler from '@Root/ErrorHandler';
import LabelCardService from '@Helpers/URLHelpers/WeboutService/LabelCardService';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './LabelCardView.scss';

export default function LabelCardView(props) {
  const [labelCard, setLabelCard] = useState({});
  const [cursor, setCursor] = useState();
  const [name, setName] = useState('');
  const [editName, setEditName] = useState(false);
  const [description, setDescription] = useState('');
  const [editDescription, setEditDescription] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [editRemarks, setEditRemarks] = useState(false);
  const [edited, setEdited] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const errorHandler = new ErrorHandler();
  const labelCardService = new LabelCardService();
  const cn = 'labelCardView';

  useEffect(() => {
    parseData();
  }, [props]);

  const parseData = () => {
    setLabelCard(props.labelCard);
    setName(props.labelCard?.name || '');
    setDescription(props.labelCard?.description || '');
    setRemarks(props.labelCard?.remarks || '');
  };

  const genericToggle = (flagSetter, flag, submit = true) => {
    if (flag && submit) handleSubmit();
    flagSetter(!flag);
  };

  const handleNameChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setName(sanitized);
    labelCard.name = sanitized;
    setEdited(true);
  };

  const handleDescriptionChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setDescription(sanitized);
    labelCard.description = sanitized;
    setEdited(true);
  };

  const handleRemarksChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setRemarks(sanitized);
    labelCard.remarks = sanitized;
    setEdited(true);
  };

  const handleSubmit = async (e) => {
    try {
      e?.preventDefault();
      setCursor('wait');
      setShowLoader(true);
      const body = { labelCard };
      await labelCardService.updateLabelCard(body);
      await new Promise((res) => setTimeout(res, 300));
      props.reloadData();
    } catch (err) {
      errorHandler.handleError(err);
    }
    setCursor('auto');
    setShowLoader(false);
    props.closePopup();
  };

  const getLabelColor = (n) => {
    if (!n) return '#1976d2';
    const hash = n.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    const colors = ['#1976d2', '#2e7d32', '#8e24aa', '#d32f2f', '#ef6c00', '#0288d1'];
    return colors[hash % colors.length];
  };

  const labelColor = getLabelColor(name);

  // ─── Sub-components ──────────────────────────────────────────────────────────

  const Section = ({ title, icon, children }) => (
    <Box className={`${cn}__section`}>
      <Box className={`${cn}__section__head`}>
        {icon}
        <span>{title}</span>
      </Box>
      <Box className={`${cn}__section__body`}>
        {children}
      </Box>
    </Box>
  );

  const FieldRow = ({ label, editing, toggle, valueDisplay, children }) => (
    <Box className={`${cn}__fieldRow ${editing ? `${cn}__fieldRow--editing` : ''}`}>
      <Box className={`${cn}__fieldRow__labelCol`}>
        <Typography className={`${cn}__fieldRow__label`}>{label}</Typography>
      </Box>
      <Box className={`${cn}__fieldRow__valueCol`}>
        {editing ? (
          <Box className={`${cn}__fieldRow__input`} onBlur={toggle}>
            {children}
          </Box>
        ) : (
          <Typography
            className={`${cn}__fieldRow__value ${!valueDisplay ? `${cn}__muted` : ''}`}
            onClick={toggle}
          >
            {valueDisplay || '—'}
          </Typography>
        )}
      </Box>
      <Tooltip title={editing ? 'Save' : 'Edit'}>
        <IconButton size="small" onClick={toggle} className={`${cn}__editBtn`}>
          <Edit />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Modal open={props.open} onClose={props.closePopup} style={{ cursor }}>
      <div className={cn}>
        <div className={`${cn}__panel height-wfa`}>

          {/* ── Hero ─────────────────────────────────────────────────────── */}
          <Box className={`${cn}__hero`} style={{ '--label-color': labelColor }}>
            <Avatar
              className={`${cn}__hero__avatar`}
              style={{ background: labelColor }}
              variant="rounded"
            >
              <LabelIcon className={`${cn}__hero__avatarIcon`} />
            </Avatar>
            <Typography className={`${cn}__hero__name`}>
              {name || 'Label Card'}
            </Typography>
            {description && (
              <Typography className={`${cn}__hero__meta`} noWrap>
                {description.length > 60 ? `${description.slice(0, 60)}…` : description}
              </Typography>
            )}
            <IconButton onClick={props.closePopup} className={`${cn}__closeBtn`}>
              <Close />
            </IconButton>
          </Box>

          {/* ── Form ─────────────────────────────────────────────────────── */}
          {showLoader ? (
            <Box className={`${cn}__loader`}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <form onSubmit={handleSubmit} className={`${cn}__form`}>

              <Section title="Details" icon={<DetailsIcon />}>
                <FieldRow
                  label="Name"
                  editing={editName}
                  toggle={() => genericToggle(setEditName, editName)}
                  valueDisplay={name}
                >
                  <TextField
                    value={name}
                    onChange={handleNameChange}
                    size="small"
                    fullWidth
                    placeholder="Enter label name"
                    inputProps={{ maxLength: 200 }}
                    autoFocus
                    required
                  />
                </FieldRow>
                <FieldRow
                  label="Description"
                  editing={editDescription}
                  toggle={() => genericToggle(setEditDescription, editDescription)}
                  valueDisplay={description}
                >
                  <TextField
                    value={description}
                    onChange={handleDescriptionChange}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    placeholder="Enter description"
                    autoFocus
                  />
                </FieldRow>
              </Section>

              <Section title="Notes" icon={<RemarksIcon />}>
                <FieldRow
                  label="Remarks"
                  editing={editRemarks}
                  toggle={() => genericToggle(setEditRemarks, editRemarks)}
                  valueDisplay={remarks}
                >
                  <TextField
                    value={remarks}
                    onChange={handleRemarksChange}
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={6}
                    placeholder="Enter any remarks"
                    autoFocus
                  />
                </FieldRow>
              </Section>

            </form>
          )}

          {/* ── Actions ──────────────────────────────────────────────────── */}
          <Box className={`${cn}__actions`}>
            <BorderBlueButtonV2
              text="Update"
              onClick={handleSubmit}
              disabled={cursor === 'wait' || showLoader || !edited}
            />
            <BorderWhiteButtonV2
              text="Cancel"
              onClick={props.closePopup}
              disabled={cursor === 'wait'}
            />
          </Box>

        </div>
      </div>
    </Modal>
  );
}
